import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	verifyGroupOwnership,
	verifyGroupMembershipPermission,
} from "@/lib/security-utils";
import {
	getErrorMessage,
	getUserFriendlyErrorMessage,
} from "@/utils/error-utils";
import {
	FetchMenteesResponse,
	FetchMenteesRequest,
} from "@/types/supabase-functions";
import type { FetchMenteesMentee } from "@/types/supabase-functions";
import { TIME_CONSTANTS } from "@/lib/constants";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Badge,
	Progress,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem,
	SelectValue,
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	AlertDialog,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel,
} from "@/components/primitives";
import {
	Users,
	Target,
	Eye,
	ArrowUpDown,
	ArrowUp,
	ArrowDown,
	Trash2,
	ArrowLeft,
} from "lucide-react";
import { TeamMoodOverview } from "@/features/terms/TeamMoodOverview";
import { TopicAssignment, MentorNote } from "@/types/mentor";

// Database row types that match what Supabase actually returns
interface GroupRow {
	id: string;
	name: string | null;
	description: string | null;
	created_at: string | null;
	created_by: string | null;
	updated_at: string | null;
}

interface TopicRow {
	id: string;
	name: string | null;
	title?: string | null;
	description: string | null;
	created_at: string | null;
	created_by: string | null;
	prompt_creation?: string | null;
	initial_question?: string | null;
	color?: string | null;
	updated_at?: string | null;
}

interface GroupMemberRow {
	id: string;
	mentee_id: string | null;
	mentee_name: string;
	mentee_email: string;
	joined_at: string | null;
}

// Extended TopicAssignment type that includes the topics relation from Supabase queries
interface TopicAssignmentWithTopic extends TopicAssignment {
	topics?: {
		name: string;
		description: string;
	};
}

// Group note type (for group_notes table if needed)
interface GroupNote {
	id: string;
	group_id: string;
	notes: string;
	created_at: string;
	updated_at: string;
}

export default function GroupDetail() {
	const { groupId } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();

	const [group, setGroup] = useState<GroupRow | null>(null);
	const [members, setMembers] = useState<GroupMemberRow[]>([]);
	const [assignments, setAssignments] = useState<TopicAssignmentWithTopic[]>(
		[],
	);
	const [topics, setTopics] = useState<TopicRow[]>([]);
	const [allTopicsForDropdown, setAllTopicsForDropdown] = useState<TopicRow[]>(
		[],
	);
	const [allMentees, setAllMentees] = useState<FetchMenteesMentee[]>([]);
	const [groupNote, setGroupNote] = useState<GroupNote | null>(null);
	const [loading, setLoading] = useState(true);
	const [latestReflectionByUser, setLatestReflectionByUser] = useState<
		Record<string, string>
	>({});
	const [mentorNotes, setMentorNotes] = useState<Record<string, MentorNote>>(
		{},
	);

	// Topic assignment UI state
	const [showAssignTopicDialog, setShowAssignTopicDialog] = useState(false);
	const [selectedTopicId, setSelectedTopicId] = useState<string>("");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [sortField, setSortField] = useState<
		"name" | "reflections" | "last_activity"
	>("name");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	useEffect(() => {
		if (!groupId) return;
		fetchGroup();
	}, [groupId]);

	const fetchGroup = async () => {
		try {
			setLoading(true);

			const { data: groupData, error: groupErr } = await supabase
				.from("groups")
				.select("*")
				.eq("id", groupId)
				.maybeSingle();

			if (groupErr || !groupData) {
				console.error("Error fetching group:", groupErr);
				setGroup(null);
				return;
			}

			setGroup(groupData);

			// memberships
			// NOTE: there are no entries here??
			const { data: memberships } = await supabase
				.from("group_memberships")
				.select("id, mentee_id, joined_at")
				.eq("group_id", groupId);

			console.log("memberships", memberships);

			const memberIds = (memberships || []).map((m) => m.mentee_id);

			// fetch mentees via edge function
			const { data: menteesResp } =
				await supabase.functions.invoke<FetchMenteesResponse>("fetch-mentees");
			const allMenteesResp = menteesResp?.mentees || [];
			setAllMentees(allMenteesResp);

			const groupMembers: GroupMemberRow[] = (memberships || []).map((m) => {
				const profile = allMenteesResp.find(
					(x: FetchMenteesMentee) => x.id === m.mentee_id,
				);
				return {
					id: m.id,
					mentee_id: m.mentee_id,
					mentee_name: profile?.name || "Unknown",
					mentee_email: profile?.email || "Unknown",
					joined_at: m.joined_at,
				};
			});
			setMembers(groupMembers);

			// Use service-edge returned latest reflection ids (overall and topic-specific) to avoid RLS issues
			try {
				const map: Record<string, string> = {};
				for (const gm of groupMembers) {
					if (!gm.mentee_id) continue;
					const profile = allMenteesResp.find(
						(x: FetchMenteesMentee) => x.id === gm.mentee_id,
					);
					// Prefer topic-specific latest reflection if provided by the edge function, otherwise use overall latest
					const latest = profile?.latest_topic_reflection_id;
					if (latest) map[gm.mentee_id] = latest;
				}
				setLatestReflectionByUser(map);

				// Fetch mentor notes for review status
				await fetchMentorNotes(Object.values(map));
			} catch (err) {
				console.debug(
					"Error mapping latest reflections from service response",
					err,
				);
				setLatestReflectionByUser({});
			}

			// assignments: include assignments explicitly for this group OR assignments for members of this group
			let assignData: any[] = [];
			try {
				const base = supabase
					.from("topic_assignments")
					.select("*, topics!inner(name, description)")
					.eq("topics.created_by", user?.id)
					.order("created_at", { ascending: false });

				// Only fetch assignments explicitly created for this group (group-scoped assignments).
				const { data: fetchedAssignments, error: assignError } = await base.eq(
					"group_id",
					groupId,
				);
				if (assignError) {
					console.error("Error fetching assignments for group:", assignError);
					setAssignments([]);
				} else {
					assignData = fetchedAssignments || [];
					setAssignments(assignData);
				}
			} catch (err) {
				console.error("Error fetching assignments for group (catch):", err);
				setAssignments([]);
			}

			const topicIds = Array.from(
				new Set((assignData || []).map((a) => a.topic_id).filter(Boolean)),
			);
			if (topicIds.length > 0) {
				const { data: topicsData } = await supabase
					.from("topics")
					.select("*")
					.in("id", topicIds);
				setTopics((topicsData || []) as TopicRow[]);

				// Re-fetch mentees via the service-edge function to obtain topic-specific latest reflection ids
				try {
					const invokePayload: FetchMenteesRequest = { topicIds };
					const { data: menteesWithLatest } =
						await supabase.functions.invoke<FetchMenteesResponse>(
							"fetch-mentees",
							{ body: JSON.stringify(invokePayload) },
						);
					const allMenteesResp2 = menteesWithLatest?.mentees || allMenteesResp;
					setAllMentees(allMenteesResp2);

					const map: Record<string, string> = {};
					for (const gm of groupMembers) {
						const profile = allMenteesResp2.find(
							(x: FetchMenteesMentee) => x.id === gm.mentee_id,
						);
						const latest = profile?.latest_topic_reflection_id;
						if (latest) map[gm.mentee_id] = latest;
					}
					setLatestReflectionByUser(map);

					// Fetch mentor notes for review status
					await fetchMentorNotes(Object.values(map));
				} catch (err) {
					console.debug(
						"Error fetching mentees with topic-specific reflections",
						err,
					);
				}
			} else {
				setTopics([]);
				// No topics assigned -> clear topic-specific reflections map
				setLatestReflectionByUser({});
			}

			// Fetch all topics created by this mentor to populate assign dropdown (separate state)
			try {
				const { data: allTopics } = await supabase
					.from("topics")
					.select("*")
					.eq("created_by", user?.id)
					.order("created_at", { ascending: false });
				setAllTopicsForDropdown(allTopics || []);
			} catch (err) {
				console.debug("Error fetching mentor topics for assignment", err);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setLoading(false);
		}
	};

	const fetchMentorNotes = async (reflectionIds: string[]) => {
		if (reflectionIds.length === 0) {
			setMentorNotes({});
			return;
		}

		try {
			const { data: notes, error } = await supabase
				.from("mentor_notes")
				.select("reflection_id, reviewed, notes, created_at, updated_at")
				.in("reflection_id", reflectionIds)
				.eq("mentor_id", user?.id);

			if (error) {
				console.error("Error fetching mentor notes:", error);
				setMentorNotes({});
				return;
			}

			const notesMap: Record<string, MentorNote> = {};
			(notes || []).forEach((note) => {
				notesMap[note.reflection_id] = note;
			});
			setMentorNotes(notesMap);
		} catch (err) {
			console.error("Error fetching mentor notes:", err);
			setMentorNotes({});
		}
	};

	const assignTopicToGroupLocal = async () => {
		if (!selectedTopicId || !group) return;
		const groupMentees = members || [];

		try {
			// Check if a group-level assignment already exists
			const { data: existingGroupAssignment, error: groupCheckErr } =
				await supabase
					.from("topic_assignments")
					.select("id", "mentee_id")
					.eq("topic_id", selectedTopicId)
					.eq("group_id", group.id)
					.returns<{ id: string; mentee_id: string }[]>();

			if (groupCheckErr) {
				console.error(
					"Error checking existing group assignment:",
					groupCheckErr,
				);
				throw groupCheckErr;
			}

			const missingLinks = groupMentees.filter(
				(m) =>
					!existingGroupAssignment.some((e) => e.mentee_id === m.mentee_id),
			);
			const { error } = await supabase.from("topic_assignments").insert(
				missingLinks.map((row) => ({
					topic_id: selectedTopicId,
					mentee_id: row.mentee_id,
					assigned_by: user?.id,
					group_id: group.id,
				})),
			);

			if (error) throw error;

			alert("Assigned topic to group.");
			setSelectedTopicId("");
			setShowAssignTopicDialog(false);
			await fetchGroup();
		} catch (err) {
			console.error("Error assigning topic:", err);
			alert("Error assigning topic: " + (err as any)?.message || err);
		}
	};

	const unassignTopicFromGroup = async (topicId: string) => {
		if (!group) return;
		if (
			!confirm(
				"Remove this topic from the group? This will remove assignments created for the group.",
			)
		)
			return;
		try {
			// First fetch assignment ids that match this topic and group
			const { data: assignmentsToRemove, error: selectErr } = await supabase
				.from("topic_assignments")
				.select("id, mentee_id")
				.eq("topic_id", topicId)
				.eq("group_id", group.id);

			console.debug("GroupDetail: assignmentsToRemove fetched", {
				topicId,
				groupId: group.id,
				assignmentsToRemove,
				selectErr,
			});

			if (selectErr) {
				console.error("Error fetching assignments to remove:", selectErr);
				alert("Failed to unassign topic");
				return;
			}

			const ids = (assignmentsToRemove || []).map((a) => a.id).filter(Boolean);
			if (ids.length === 0) {
				// Nothing to delete
				await fetchGroup();
				return;
			}

			// Split ids by whether the row had a mentee_id (non-null) or was a group-level assignment (mentee_id null)
			const idsWithMentee = (assignmentsToRemove || [])
				.filter((a) => a.mentee_id)
				.map((a) => a.id)
				.filter(Boolean);
			const idsNullMentee = (assignmentsToRemove || [])
				.filter((a) => !a.mentee_id)
				.map((a) => a.id)
				.filter(Boolean);

			// Delete non-null mentee assignments first
			if (idsWithMentee.length > 0) {
				const { error: deleteErr } = await supabase
					.from("topic_assignments")
					.delete()
					.in("id", idsWithMentee);

				if (deleteErr) {
					console.error(
						"Error deleting mentee-specific assignments by id:",
						deleteErr,
					);
					alert("Failed to unassign topic");
					return;
				}
			}

			// Then delete any group-level assignment rows (mentee_id IS NULL)
			if (idsNullMentee.length > 0) {
				try {
					// Try delete by ids first
					const { error: deleteNullErr } = await supabase
						.from("topic_assignments")
						.delete()
						.in("id", idsNullMentee);

					if (deleteNullErr) {
						console.warn(
							"Delete by ids for null-mentee rows failed, attempting delete by filter",
							deleteNullErr,
						);
						// Fallback: delete by matching topic_id, group_id and mentee_id IS NULL
						const { error: deleteFilterErr } = await supabase
							.from("topic_assignments")
							.delete()
							.eq("topic_id", topicId)
							.eq("group_id", group.id)
							.is("mentee_id", null);

						if (deleteFilterErr) {
							console.error(
								"Failed to delete group-level assignment via filter:",
								deleteFilterErr,
							);
							alert("Failed to unassign topic");
							return;
						}
					}
				} catch (err) {
					console.error("Error deleting group-level assignment:", err);
					alert("Failed to unassign topic");
					return;
				}
			}

			await fetchGroup();
		} catch (err) {
			console.error("Error unassigning topic:", err);
			alert("Failed to unassign topic");
		}
	};

	const removeMenteeFromGroup = useCallback(
		async (membershipId: string) => {
			if (!membershipId || !user?.id) return;
			try {
				// Use security utility to verify permission
				const { hasPermission } = await verifyGroupMembershipPermission(
					user.id,
					membershipId,
				);
				if (!hasPermission) {
					alert(
						"Unauthorized: you do not have permission to remove this mentee.",
					);
					return;
				}

				const { error } = await supabase
					.from("group_memberships")
					.delete()
					.eq("id", membershipId);
				if (error) {
					const errorMsg = getUserFriendlyErrorMessage(
						error,
						"removing mentee from group",
					);
					alert(errorMsg);
				} else {
					await fetchGroup();
				}
			} catch (err) {
				console.error(err);
				const errorMsg = getUserFriendlyErrorMessage(
					err,
					"removing mentee from group",
				);
				alert(errorMsg);
			}
		},
		[user?.id],
	);

	const getReflectionSortKey = (menteeId: string | null | undefined) => {
		if (!menteeId) return 2; // pending
		const reflectionId = latestReflectionByUser[menteeId];
		if (!reflectionId) return 2; // pending
		const isReviewed = !!mentorNotes[reflectionId]?.reviewed;
		return isReviewed ? 1 : 0; // 0 awaiting, 1 reviewed
	};

	const getLastActivityValue = (menteeId: string | null | undefined) => {
		if (!menteeId) return 0;
		const m = allMentees.find((x) => x.id === menteeId);
		if (!m?.last_activity) return 0;
		const d = new Date(m.last_activity);
		return isNaN(d.getTime()) ? 0 : d.getTime();
	};

	const handleSort = (field: "name" | "reflections" | "last_activity") => {
		if (sortField === field)
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const getSortIcon = (field: "name" | "reflections" | "last_activity") => {
		if (sortField !== field)
			return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
		return sortDirection === "asc" ? (
			<ArrowUp className="w-3 h-3" />
		) : (
			<ArrowDown className="w-3 h-3" />
		);
	};

	// Memoize assignment details to avoid recalculating on every render
	const computeAssignmentDetails = useMemo(() => {
		const totalMentees = members.length;
		const memberIds = new Set(members.map((m) => m.mentee_id).filter(Boolean));

		// consider assignments that explicitly target this group only
		const relevantAssignments = (assignments || []).filter(
			(a) => a.group_id === groupId,
		);

		const topicIds = Array.from(
			new Set(relevantAssignments.map((a) => a.topic_id)),
		);

		return topicIds.map((tid) => {
			const topic =
				topics.find((t) => t.id === tid) ||
				(relevantAssignments.find((a) => a.topic_id === tid)?.topics ?? null);

			// Count unique mentees in this group assigned to this topic (only assignments created for this group)
			const assignedMenteeIds = new Set(
				relevantAssignments
					.filter((a) => a.topic_id === tid && a.mentee_id)
					.map((a) => a.mentee_id)
					.filter(Boolean),
			);

			const assignedCount = assignedMenteeIds.size;
			const completionPercentage =
				totalMentees > 0 ? Math.round((assignedCount / totalMentees) * 100) : 0;

			return {
				topic_id: tid,
				title: topic?.name || topic?.title || "Unknown Topic",
				description: topic?.description || "",
				assignedCount,
				totalMentees,
				completionPercentage,
			};
		});
	}, [members, assignments, topics, groupId]);

	if (loading) return <div className="text-center py-8">Loading...</div>;
	if (!group) return <div className="text-center py-8">Group not found</div>;

	const details = computeAssignmentDetails;

	const displayedMembers = [...members]
		.filter((m) => {
			const term = searchTerm.toLowerCase().trim();
			if (!term) return true;
			return (
				m.mentee_name.toLowerCase().includes(term) ||
				m.mentee_email.toLowerCase().includes(term)
			);
		})
		.sort((a, b) => {
			if (sortField === "name") {
				const av = a.mentee_name.toLowerCase();
				const bv = b.mentee_name.toLowerCase();
				if (av < bv) return sortDirection === "asc" ? -1 : 1;
				if (av > bv) return sortDirection === "asc" ? 1 : -1;
				return 0;
			}
			if (sortField === "reflections") {
				const ak = getReflectionSortKey(a.mentee_id ?? null);
				const bk = getReflectionSortKey(b.mentee_id ?? null);
				if (ak < bk) return sortDirection === "asc" ? -1 : 1;
				if (ak > bk) return sortDirection === "asc" ? 1 : -1;
				return a.mentee_name.localeCompare(b.mentee_name);
			}
			const av = getLastActivityValue(a.mentee_id ?? null);
			const bv = getLastActivityValue(b.mentee_id ?? null);
			if (av < bv) return sortDirection === "asc" ? -1 : 1;
			if (av > bv) return sortDirection === "asc" ? 1 : -1;
			return 0;
		});

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl">
			<div className="mb-6 flex items-start justify-between">
				<div>
					<h1 className="text-2xl">Group : {group.name}</h1>
					<p className="text-s text-muted-foreground mt-1">
						{group.description || ""} {group.description ? "•" : ""} Created:{" "}
						{new Date(group.created_at).toLocaleDateString()}
					</p>
				</div>
				<div className="hidden">
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								size="icon"
								variant="outline"
								className="hover:bg-red-50"
								aria-label="Delete group"
							>
								<Trash2 className="w-4 h-4 text-red-600" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Delete Group</AlertDialogTitle>
								<AlertDialogDescription>
									Are you sure you want to delete this group? This will remove
									all memberships and group assignments. This action cannot be
									undone.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={async () => {
										try {
											// Ensure group ownership
											const { data: groupRow } = await supabase
												.from("groups")
												.select("created_by")
												.eq("id", groupId)
												.maybeSingle();
											if (!groupRow) {
												alert("Group not found");
												return;
											}
											if (groupRow.created_by !== user?.id) {
												alert(
													"Unauthorized: only the group creator can delete the group",
												);
												return;
											}

											// Attempt to run a transactional RPC on the DB to delete the group and dependents atomically
											try {
												const { error: rpcErr } = await supabase.rpc(
													"delete_group",
													{ p_group_id: groupId },
												);
												if (!rpcErr) {
													// RPC succeeded, navigate away
													navigate("/mentor/my-groups");
													return;
												}
												console.debug(
													"RPC delete_group returned error, falling back to manual deletes",
													rpcErr,
												);
											} catch (rpcError) {
												console.debug(
													"RPC delete_group call failed, falling back to manual deletes",
													rpcError,
												);
											}

											// Try to delete topic_assignments directly by group_id first (works if permitted)
											const { error: directDelAssignErr } = await supabase
												.from("topic_assignments")
												.delete()
												.eq("group_id", groupId);
											if (directDelAssignErr) {
												// If direct delete fails due to permissions or constraints, try selecting ids as fallback
												console.debug(
													"Direct delete by group_id failed, attempting select->delete fallback",
													directDelAssignErr,
												);
												const { data: assignRows, error: fetchAssignErr } =
													await supabase
														.from("topic_assignments")
														.select("id")
														.eq("group_id", groupId);
												if (fetchAssignErr) throw fetchAssignErr;
												const assignIds = (assignRows || [])
													.map((r: { id: string }) => r.id)
													.filter(Boolean);
												if (assignIds.length > 0) {
													const { error: deleteAssignErr } = await supabase
														.from("topic_assignments")
														.delete()
														.in("id", assignIds);
													if (deleteAssignErr) throw deleteAssignErr;
												}
											}

											// Delete group memberships (try direct delete)
											const { error: directDelMembersErr } = await supabase
												.from("group_memberships")
												.delete()
												.eq("group_id", groupId);
											if (directDelMembersErr) {
												const { data: membershipRows, error: fetchMembersErr } =
													await supabase
														.from("group_memberships")
														.select("id")
														.eq("group_id", groupId);
												if (fetchMembersErr) throw fetchMembersErr;
												const membershipIds = (membershipRows || [])
													.map((r: { id: string }) => r.id)
													.filter(Boolean);
												if (membershipIds.length > 0) {
													const { error: deleteMembersErr } = await supabase
														.from("group_memberships")
														.delete()
														.in("id", membershipIds);
													if (deleteMembersErr) throw deleteMembersErr;
												}
											}

											// Also remove any group_notes if present (ignore 404/table-missing)
											try {
												const { data: notesRows, error: notesErr } =
													await supabase
														.from("group_notes")
														.select("id")
														.eq("group_id", groupId);
												if (notesErr) {
													// If table doesn't exist, PostgREST returns 404 — ignore it
													if ((notesErr as any).status === 404) {
														console.debug(
															"group_notes table not present, skipping cleanup",
														);
													} else {
														throw notesErr;
													}
												} else if (notesRows && notesRows.length > 0) {
													const noteIds = notesRows
														.map((r: { id: string }) => r.id)
														.filter(Boolean);
													const { error: deleteNotesErr } = await supabase
														.from("group_notes")
														.delete()
														.in("id", noteIds);
													if (deleteNotesErr) throw deleteNotesErr;
												}
											} catch (e) {
												// ignore if table doesn't exist or permission issues
												console.debug(
													"group_notes cleanup skipped or failed",
													e,
												);
											}

											// Finally delete group
											const { error: delGroupErr } = await supabase
												.from("groups")
												.delete()
												.eq("id", groupId)
												.eq("created_by", user?.id);
											if (delGroupErr) {
												// If delete still fails with FK constraint, provide actionable message
												console.error("Final group delete failed", delGroupErr);
												if (delGroupErr.code === "23503") {
													alert(
														"Cannot delete group: there are still related records referencing this group (e.g. topic_assignments). Please remove related assignments or enable ON DELETE CASCADE in your database schema.",
													);
												} else {
													throw delGroupErr;
												}
											}

											navigate("/mentor/my-groups");
										} catch (err) {
											console.error("Error deleting group:", err);
											alert("Failed to delete group.");
										}
									}}
								>
									Delete Group
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			<div className="mb-6 flex items-center justify-between">
				<Button
					variant="ghost"
					onClick={() =>
						window.history.length > 1
							? navigate(-1)
							: navigate("/mentor/my-groups")
					}
				>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Groups
				</Button>
				<div className="flex items-center space-x-2">
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								size="icon"
								variant="outline"
								className="hover:bg-red-50"
								aria-label="Delete group"
							>
								<Trash2 className="w-4 h-4 text-red-600" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Delete Group</AlertDialogTitle>
								<AlertDialogDescription>
									Are you sure you want to delete this group? This will remove
									all memberships and group assignments. This action cannot be
									undone.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Cancel</AlertDialogCancel>
								<AlertDialogAction
									onClick={async () => {
										try {
											// Ensure group ownership
											const { data: groupRow } = await supabase
												.from("groups")
												.select("created_by")
												.eq("id", groupId)
												.maybeSingle();
											if (!groupRow) {
												alert("Group not found");
												return;
											}
											if (groupRow.created_by !== user?.id) {
												alert(
													"Unauthorized: only the group creator can delete the group",
												);
												return;
											}

											// Attempt to run a transactional RPC on the DB to delete the group and dependents atomically
											try {
												const { error: rpcErr } = await supabase.rpc(
													"delete_group",
													{ p_group_id: groupId },
												);
												if (!rpcErr) {
													// RPC succeeded, navigate away
													navigate("/mentor/my-groups");
													return;
												}
												console.debug(
													"RPC delete_group returned error, falling back to manual deletes",
													rpcErr,
												);
											} catch (rpcError) {
												console.debug(
													"RPC delete_group call failed, falling back to manual deletes",
													rpcError,
												);
											}

											// Try to delete topic_assignments directly by group_id first (works if permitted)
											const { error: directDelAssignErr } = await supabase
												.from("topic_assignments")
												.delete()
												.eq("group_id", groupId);
											if (directDelAssignErr) {
												// If direct delete fails due to permissions or constraints, try selecting ids as fallback
												console.debug(
													"Direct delete by group_id failed, attempting select->delete fallback",
													directDelAssignErr,
												);
												const { data: assignRows, error: fetchAssignErr } =
													await supabase
														.from("topic_assignments")
														.select("id")
														.eq("group_id", groupId);
												if (fetchAssignErr) throw fetchAssignErr;
												const assignIds = (assignRows || [])
													.map((r: { id: string }) => r.id)
													.filter(Boolean);
												if (assignIds.length > 0) {
													const { error: deleteAssignErr } = await supabase
														.from("topic_assignments")
														.delete()
														.in("id", assignIds);
													if (deleteAssignErr) throw deleteAssignErr;
												}
											}

											// Delete group memberships (try direct delete)
											const { error: directDelMembersErr } = await supabase
												.from("group_memberships")
												.delete()
												.eq("group_id", groupId);
											if (directDelMembersErr) {
												const { data: membershipRows, error: fetchMembersErr } =
													await supabase
														.from("group_memberships")
														.select("id")
														.eq("group_id", groupId);
												if (fetchMembersErr) throw fetchMembersErr;
												const membershipIds = (membershipRows || [])
													.map((r: { id: string }) => r.id)
													.filter(Boolean);
												if (membershipIds.length > 0) {
													const { error: deleteMembersErr } = await supabase
														.from("group_memberships")
														.delete()
														.in("id", membershipIds);
													if (deleteMembersErr) throw deleteMembersErr;
												}
											}

											// Also remove any group_notes if present (ignore 404/table-missing)
											try {
												const { data: notesRows, error: notesErr } =
													await supabase
														.from("group_notes")
														.select("id")
														.eq("group_id", groupId);
												if (notesErr) {
													// If table doesn't exist, PostgREST returns 404 — ignore it
													if ((notesErr as any).status === 404) {
														console.debug(
															"group_notes table not present, skipping cleanup",
														);
													} else {
														throw notesErr;
													}
												} else if (notesRows && notesRows.length > 0) {
													const noteIds = notesRows
														.map((r: { id: string }) => r.id)
														.filter(Boolean);
													const { error: deleteNotesErr } = await supabase
														.from("group_notes")
														.delete()
														.in("id", noteIds);
													if (deleteNotesErr) throw deleteNotesErr;
												}
											} catch (e) {
												// ignore if table doesn't exist or permission issues
												console.debug(
													"group_notes cleanup skipped or failed",
													e,
												);
											}

											// Finally delete group
											const { error: delGroupErr } = await supabase
												.from("groups")
												.delete()
												.eq("id", groupId)
												.eq("created_by", user?.id);
											if (delGroupErr) {
												// If delete still fails with FK constraint, provide actionable message
												console.error("Final group delete failed", delGroupErr);
												if (delGroupErr.code === "23503") {
													alert(
														"Cannot delete group: there are still related records referencing this group (e.g. topic_assignments). Please remove related assignments or enable ON DELETE CASCADE in your database schema.",
													);
												} else {
													throw delGroupErr;
												}
											}

											navigate("/mentor/my-groups");
										} catch (err) {
											console.error("Error deleting group:", err);
											alert("Failed to delete group.");
										}
									}}
								>
									Delete Group
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>

			<div className="space-y-4">
				<Card>
					<CardHeader>
						<CardTitle>Team Mood</CardTitle>
						<CardDescription>Overview of recent moods</CardDescription>
					</CardHeader>
					<CardContent>
						<TeamMoodOverview userIds={members.map((m) => m.mentee_id)} />
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="relative flex items-start justify-between">
						<div className="flex-1">
							<CardTitle>Assigned Topics</CardTitle>
							<CardDescription>
								Reflection topics assigned to this group
							</CardDescription>
						</div>
						<div className="absolute top-4 right-4">
							<Dialog
								open={showAssignTopicDialog}
								onOpenChange={setShowAssignTopicDialog}
							>
								<DialogTrigger asChild>
									<Button
										size="sm"
										disabled={members.length === 0}
										variant={details.length === 0 ? "default" : "outline"}
										className={
											details.length === 0
												? "bg-green-600 hover:bg-green-700 text-white"
												: ""
										}
									>
										+ Assign new topic
									</Button>
								</DialogTrigger>
								<DialogContent className="sm:max-w-md">
									<DialogHeader>
										<DialogTitle>Assign Topic to {group?.name}</DialogTitle>
									</DialogHeader>
									<div className="space-y-4">
										<Select
											value={
												selectedTopicId === "new_topic"
													? "new_topic"
													: selectedTopicId
											}
											onValueChange={(value) => {
												if (value === "new_topic") {
													setShowAssignTopicDialog(false);
													navigate("/mentor/topic/new");
												} else {
													setSelectedTopicId(value);
												}
											}}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select a topic" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="new_topic">
													+ Create new topic
												</SelectItem>
												{(() => {
													const assignedTopicIds = new Set(
														(assignments || []).map((a) => a.topic_id),
													);
													return (allTopicsForDropdown || []).map((t) => {
														const id = t.id;
														const label = t.name || t.title || "Untitled Topic";
														const isAssigned = assignedTopicIds.has(id);
														return (
															<SelectItem
																key={id}
																value={id}
																disabled={isAssigned}
															>
																{label}
																{isAssigned ? " (assigned)" : ""}
															</SelectItem>
														);
													});
												})()}
											</SelectContent>
										</Select>

										<div className="flex justify-end space-x-2">
											<Button
												variant="outline"
												onClick={() => {
													setSelectedTopicId("");
													setShowAssignTopicDialog(false);
												}}
											>
												Cancel
											</Button>
											<Button
												onClick={assignTopicToGroupLocal}
												disabled={
													!selectedTopicId || selectedTopicId === "new_topic"
												}
											>
												Assign
											</Button>
										</div>
									</div>
								</DialogContent>
							</Dialog>
						</div>
					</CardHeader>
					<CardContent>
						{details.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								No topics assigned to this group
							</p>
						) : (
							<div className="space-y-2">
								{details.map((t) => (
									<div key={t.topic_id} className="p-3 rounded bg-muted/5">
										<div className="flex items-start justify-between">
											<div className="flex-1 pr-4">
												<div className="flex items-start justify-between">
													<div className="flex items-center space-x-2">
														<div className="font-semibold">{t.title}</div>
														<Badge className="bg-green-100 text-green-800 text-xs">
															Active
														</Badge>
													</div>
													<div className="ml-4">
														<Button
															size="sm"
															onClick={() =>
																navigate(
																	`/mentor/group/${group.id}/topic/${t.topic_id}`,
																)
															}
															className="ml-2"
														>
															View Results
														</Button>
													</div>
												</div>

												{t.description && (
													<p className="text-sm text-muted-foreground mt-1">
														{t.description}
													</p>
												)}
												<div className="flex items-center justify-between text-sm text-muted-foreground mt-2">
													<span>
														{t.assignedCount}/{t.totalMentees} assigned
													</span>
													<span>{t.completionPercentage}%</span>
												</div>
												<Progress
													value={t.completionPercentage}
													className="mt-2"
												/>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg flex items-center space-x-2">
							<Users className="w-5 h-5" />
							<span>Group Members ({members.length})</span>
						</CardTitle>
						<CardDescription>Members of this group </CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-between mb-3">
							<h4 className="text-sm font-medium flex items-center space-x-2">
								<Users className="w-4 h-4" />
								<span>Members ({members.length})</span>
								<Input
									placeholder="Filter by name or email"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="h-9 w-64"
								/>
							</h4>
							<div className="flex items-center gap-2">
								{group?.id && (
									<Button
										className="ml-2"
										variant="outline"
										onClick={() =>
											navigate(`/mentor/group/${group.id}/members`)
										}
									>
										Manage Members
									</Button>
								)}
							</div>
						</div>

						{members.length === 0 ? (
							<p className="text-sm text-muted-foreground py-3 text-center">
								No members yet
							</p>
						) : (
							<div className="rounded-md border overflow-auto">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-[200px]">
												<button
													className="flex items-center gap-1"
													onClick={() => handleSort("name")}
												>
													Name {getSortIcon("name")}
												</button>
											</TableHead>
											<TableHead className="w-[250px]">Email</TableHead>
											<TableHead className="w-[120px]">Member</TableHead>
											<TableHead className="w-[120px]">Status</TableHead>
											<TableHead className="w-[120px]">
												<button
													className="flex items-center gap-1"
													onClick={() => handleSort("reflections")}
												>
													Reflections {getSortIcon("reflections")}
												</button>
											</TableHead>
											<TableHead className="w-[150px]">
												<button
													className="flex items-center gap-1"
													onClick={() => handleSort("last_activity")}
												>
													Last Activity {getSortIcon("last_activity")}
												</button>
											</TableHead>
											<TableHead className="w-[100px]">Report</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{displayedMembers.map((member) => {
											const mentee = allMentees.find(
												(m) => m.id === member.mentee_id,
											);
											return (
												<TableRow key={member.id} className="hover:bg-muted/50">
													<TableCell className="font-medium">
														{member.mentee_name}
													</TableCell>
													<TableCell>{member.mentee_email}</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="sm"
															onClick={() =>
																member.mentee_id &&
																navigate(`/mentee/${member.mentee_id}`)
															}
															className="text-primary hover:text-primary"
														>
															<Eye className="w-4 h-4" />
														</Button>
													</TableCell>
													<TableCell>
														<span className="text-sm">Accepted</span>
													</TableCell>
													<TableCell>
														{(() => {
															const reflectionId = member.mentee_id
																? latestReflectionByUser[member.mentee_id]
																: undefined;
															if (!reflectionId) {
																return (
																	<span className="text-sm text-muted-foreground">
																		pending
																	</span>
																);
															}

															const mentorNote = mentorNotes[reflectionId];
															const isReviewed = mentorNote?.reviewed || false;

															if (isReviewed) {
																return (
																	<Badge className="bg-green-100 text-green-800 text-xs">
																		Review Completed
																	</Badge>
																);
															} else {
																return (
																	<Badge className="bg-orange-100 text-orange-800 text-xs">
																		Awaiting review
																	</Badge>
																);
															}
														})()}
													</TableCell>
													<TableCell>
														<span className="text-xs text-muted-foreground">
															{mentee?.last_activity
																? new Date(
																		mentee.last_activity,
																	).toLocaleDateString()
																: "-"}
														</span>
													</TableCell>
													<TableCell>
														{latestReflectionByUser[member.mentee_id] ? (
															<Button
																size="sm"
																variant="outline"
																onClick={() =>
																	navigate(
																		`/mentor/reflection/${latestReflectionByUser[member.mentee_id]}`,
																	)
																}
															>
																View
															</Button>
														) : null}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
